import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'internal';
}

/**
 * Route guard that redirects unauthenticated users to the login page.
 * Optionally enforces a minimum role level.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EDE6DE]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-3 border-[#C5922E] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[#6B7280] font-medium tracking-wide">
            Verifying authentication...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role-based access control
  if (requiredRole && user) {
    const roleHierarchy: Record<string, number> = {
      admin: 3,
      internal: 2,
      external: 1,
    };

    const userLevel = roleHierarchy[user.role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;

    if (userLevel < requiredLevel) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-[#EDE6DE]">
          <div className="text-center max-w-md p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.834-2.694-.834-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-display font-semibold text-[#1B2A4A] mb-2">
              Access Restricted
            </h2>
            <p className="text-[#6B7280] text-sm">
              You do not have permission to access this section.
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
